import { Button, Input, Notice } from 'src/design-system'
import { observer } from 'mobx-react-lite'
import { ZodError } from 'zod'
import * as React from 'react'
import { CheckinCreateError } from './checkin-create-error'
import { CheckinFormFooter } from './checkin-form-footer'
import { CheckinFormVm } from './checkin-form-vm'
import { Editor, EditorContext } from 'components/atoms/editor'
import { PositionSelect } from 'components/position-select'
import { SkillSelect } from 'components/skill-select'
import { UnfinishedCheckinsLink } from './unfinished-checkins-link'
import { UserSelect } from 'components/user-select'
import { store } from 'store/index'
import { useStore } from 'store/context'

// The `title` column is an unbounded varchar at the DB level, so this is a
// product-level cap that keeps the field sane.
const TITLE_MAX_LENGTH = 255
// A valid title must contain at least one letter or number (any script) — i.e.
// it can't be composed solely of whitespace, punctuation, or other symbols.
const TITLE_HAS_CONTENT = /[\p{L}\p{N}]/u

export type CheckinFormProps = {
  source: string
  forUserId?: string
}

export const CheckinForm = observer((props: CheckinFormProps) => {
  const { source, forUserId } = props

  const { currentUser, positions, featureFlags, teams } = useStore()

  // Refresh team data when the form opens so joint review settings are always current.
  // This prevents stale jointReviewEnabled values from showing the toggle incorrectly.
  React.useEffect(() => {
    if (!teams.loading) {
      // eslint-disable-next-line promise/prefer-await-to-then
      teams.fetchAll({ page: { size: 9999 } }).catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const initialPositionId = forUserId
    ? store.users.byId(forUserId)?.position?.id
    : currentUser?.position?.id

  const [selectedPositionId, setSelectedPositionId] = React.useState<
    string | undefined | null
  >(initialPositionId)

  const editor = React.useRef<EditorContext>(null)

  const vm = React.useMemo(() => {
    return new CheckinFormVm(selectedPositionId, source, forUserId)
  }, [selectedPositionId, source, forUserId])

  const [formError, setFormError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [jointReview, setJointReview] = React.useState(false)
  const [title, setTitle] = React.useState('')

  const onSubmit = React.useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (vm.disabled) return
      // Mandatory fields: a position must be selected and a Title entered.
      if (!selectedPositionId || title.trim() === '') return
      // A title of only special characters/whitespace isn't meaningful.
      if (!TITLE_HAS_CONTENT.test(title)) {
        setFormError('Please enter a title that includes letters or numbers.')
        return
      }
      setFormError(null)
      setIsSubmitting(true)

      try {
        const checkin = await vm?.onSubmit(e, jointReview)
        editor.current?.reset()
        setFormError(null)
        if (checkin) location.assign(checkin.path)
      } catch (errors) {
        if (errors instanceof ZodError) {
          setFormError(
            'Please select assessor and position you want to Check-in for.',
          )
        } else if (errors instanceof CheckinCreateError) {
          // Prefer the server's own message ("Assessor must be active and belong
          // to the same organisation") over the generic fallback.
          setFormError(
            errors.userMessage ??
              'We could not start this Check-in. Please try again.',
          )
        } else if (
          errors instanceof Error &&
          errors.message === 'position-has-no-skills'
        ) {
          setFormError('Selected position has no skills.')
        } else {
          setFormError('Something went wrong. Please try again.')
        }

        // TODO: better error handling
        console.error({ errors })
      } finally {
        setIsSubmitting(false)
      }
    },
    [vm, jointReview, selectedPositionId, title],
  )

  const onPositionChange = React.useCallback((positionId: string | null) => {
    setSelectedPositionId(positionId)
  }, [])

  const checkinSkillSelect = featureFlags.featureEnabled('checkin_skill_select')

  if (!vm) return null

  return (
    <>
      <form className="pb-6" onSubmit={onSubmit} id="checkin-form">
        {vm.showJointReviewToggle && (
          // Card with background + rounded corners + horizontal margin, matching Figma
          <div className="mx-5 mb-4 rounded-lg bg-gray-50 flex items-center justify-between p-4 px-6">
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-gray-900 m-0 mb-0.5">
                Start Joint Review
              </p>
              <p className="m-0 mt-1 font-talent text-base font-normal leading-5 text-[#5E6580]">
                Streamline the review process by completing the check-in
                together, in one session.
              </p>
            </div>
            {/* Toggle — uses org brand colour (bg-theme-50) */}
            <button
              type="button"
              role="switch"
              aria-checked={jointReview}
              aria-label="Start Joint Review"
              onClick={() => setJointReview((prev) => !prev)}
              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none ml-4 ${
                jointReview ? 'bg-green-600' : 'bg-gray-300'
              }`}
            >
              <span className="sr-only">
                {jointReview ? 'Joint Review enabled' : 'Joint Review disabled'}
              </span>
              <span
                aria-hidden="true"
                className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                  jointReview ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        )}

        <div className="flex items-baseline mb-1 mx-4 relative">
          <label
            htmlFor="title"
            className="m-0 h-full absolute left-3.5 text-sm flex flex-row items-center focus-within:border-red-500"
          >
            Title
          </label>
          <Input
            name="title"
            id="title"
            placeholder="E.g. Q2 check-in"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={TITLE_MAX_LENGTH}
            naked
            className="text-sm w-full py-2 pl-[3.375rem] focus-within:border-theme-60 focus-within:!shadow-none"
          />
        </div>

        <div className="px-6">
          <UserSelect
            key="user-select-checkin"
            prefix="Assessor"
            users={vm.assessorOptions}
            isMulti={false}
            name="assessor"
            required
            defaultValue={vm.defaultAssessorOption}
            className="w-full mx-1.5 mb-1 pr-4"
          />
          <PositionSelect
            className="w-full mx-1.5 mb-4 pr-4"
            value={vm.defaultPositionId}
            isDisabled={!vm.canCheckinAgainstAnyPosition}
            onChange={onPositionChange}
            placeholder={vm.disabled ? 'No positions' : 'Select position'}
            positions={positions.loading ? [] : vm.positions}
          />
          {!vm.disabled && (
            <Editor
              autoFocus
              ref={editor}
              name="description"
              placeholder="Any particular focus areas or goals for this check-in? (optional)"
              className="mb-4 -mx-1"
              rows={2}
            />
          )}
          {vm.disabled && (
            <Notice variant="info">
              You need a position with some skills before you can start a
              Check-in. Speak to your manager or admin to create one.
            </Notice>
          )}
          {!vm.disabled && (
            <div className="flex flex-col gap-y-2 sm:flex-row items-start sm:justify-between">
              {checkinSkillSelect && (
                <div className="flex flex-row items-center gap-x-2 flex-wrap gap-y-1">
                  <SkillSelect
                    name="skill_ids"
                    skills={vm.skills}
                    triggerContent={'All skills'}
                    focusSkillIds={vm.focusSkillIds}
                  />
                </div>
              )}
              <div className="flex gap-4 ml-auto">
                {vm.unfinishedCheckinsCount > 0 && currentUser && (
                  <UnfinishedCheckinsLink
                    count={vm.unfinishedCheckinsCount}
                    user={currentUser}
                  />
                )}
                <Button
                  disabled={
                    isSubmitting || !selectedPositionId || title.trim() === ''
                  }
                  className="my-px text-base"
                  type="submit"
                >
                  Start Check-in
                </Button>
              </div>
            </div>
          )}
          {formError && <div className="mt-1 text-red-600">{formError}</div>}
        </div>
      </form>
      {vm.finalisedCheckinsCount === 0 && <CheckinFormFooter />}
    </>
  )
})

CheckinForm.displayName = 'CheckinForm'
